import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { logger } from '@/utils/logger';
import {
    LogOut,
    ArrowLeft,
    Mic,
    PenTool,
    Building2,
    CreditCard,
    Image as ImageIcon,
    Map as MapIcon,
    Grid3x3,
    ZoomIn,
    Upload,
    Trash2,
} from 'lucide-react';
import useStore from '@/store';
import type { Organization, OrgRole } from '@/types';
import { safeStorage } from '@/utils/safeStorage';
import BottomDrawer from '@/components/ui/BottomDrawer';

/**
 * Settings drawer. Yellow-tone BottomDrawer matching the rest of
 * the drawer family (project picker, floor plan picker) — black ink
 * on a yellow panel. Consistent section pattern: icon + UPPERCASE
 * header + tappable rows. Primary CTA mirrors the project drawer's
 * footer style (black pill + yellow ink) so the visual hierarchy
 * across drawers reads as one system.
 *
 * Sizes: all interactive controls cap at ~40 px height, in line with
 * the navbar's project pill / Settings button. Below the 48-px
 * Apple/Material floor, but the user explicitly asked for visual
 * consistency over the spec.
 *
 * Persists settings on close via the `pinCam:settingsChanged` event.
 * Org state is refreshed on every open so the org button reflects
 * server reality.
 */

interface SettingsBottomSheetProps {
    open: boolean;
    onClose: () => void;
}

/** Photo-watermark mode for photos taken without an active project. */
type SimplePhotoMode = 'none' | 'pincam' | 'company';

/**
 * The slice of the Zustand store this drawer touches. The store's own
 * set/get are intentionally `any` (circular-reference avoidance — see
 * src/types/store.ts), but the *consumer* surface can still be typed:
 * each selector below reads from this shape rather than `any`.
 */
interface SettingsDrawerStore {
    logout: () => void;
    handleBack: () => void;
    screen: string;
    setCurrentScreen: (name: string) => void;
    org: Organization | null;
    orgRole: OrgRole | null;
    companyLogo: string | null;
    setCompanyLogo: (logo: string | null) => void;
    fetchOrgData: () => Promise<void>;
    autoMicEnabled: boolean;
    setAutoMicEnabled: (value: boolean) => void;
    annotationModeEnabled: boolean;
    setAnnotationModeEnabled: (value: boolean) => void;
    showMicButtonOnCamera: boolean;
    setShowMicButtonOnCamera: (value: boolean) => void;
    showAnnotationButtonOnCamera: boolean;
    setShowAnnotationButtonOnCamera: (value: boolean) => void;
}

/** Narrow an unknown thrown value to a readable message. */
const errorMessage = (err: unknown): string | undefined =>
    err instanceof Error ? err.message : typeof err === 'string' ? err : undefined;

// ─── Building blocks ───────────────────────────────────────────────

const cx = (...parts: Array<string | false | undefined>): string =>
    parts.filter(Boolean).join(' ');

/** Track + thumb size used everywhere in this file. */
const ROW_MIN_HEIGHT = 'min(9vw, 40px)';

/** Visual-only toggle. State lives in the parent row button. */
const ToggleVisual: React.FC<{ enabled: boolean }> = ({ enabled }) => (
    <div
        className={cx(
            'w-10 h-5 rounded-full relative flex-shrink-0 transition-colors',
            enabled ? 'bg-black' : 'bg-black/15 border border-black/25',
        )}
        aria-hidden="true"
    >
        <div
            className={cx(
                'absolute w-4 h-4 rounded-full top-0.5 transition-transform shadow-sm',
                enabled ? 'left-5 bg-yellow-300' : 'left-0.5 bg-white',
            )}
        />
    </div>
);

/** Visual-only radio dot. State lives in the parent row button. */
const RadioDot: React.FC<{ selected: boolean }> = ({ selected }) => (
    <div
        className={cx(
            'w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors',
            selected ? 'border-black bg-black/5' : 'border-black/35',
        )}
        aria-hidden="true"
    >
        {selected && <div className="w-2 h-2 rounded-full bg-black" />}
    </div>
);

const ToggleRow: React.FC<{
    label: string;
    description?: string;
    enabled: boolean;
    onToggle: () => void;
}> = ({ label, description, enabled, onToggle }) => (
    <button
        type="button"
        role="switch"
        aria-checked={enabled}
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 px-2 py-1.5 rounded-xl hover:bg-black/5 active:bg-black/10 transition-colors text-left"
        style={{ minHeight: ROW_MIN_HEIGHT }}
    >
        <div className="flex-1 min-w-0">
            <div
                className="text-black font-medium leading-tight"
                style={{ fontSize: 'clamp(13px, 3.4vw, 15px)' }}
            >
                {label}
            </div>
            {description && (
                <div
                    className="text-black/55 mt-0.5 leading-tight"
                    style={{ fontSize: 'clamp(11px, 2.8vw, 12px)' }}
                >
                    {description}
                </div>
            )}
        </div>
        <ToggleVisual enabled={enabled} />
    </button>
);

const RadioRow: React.FC<{
    label: string;
    selected: boolean;
    onSelect: () => void;
}> = ({ label, selected, onSelect }) => (
    <button
        type="button"
        role="radio"
        aria-checked={selected}
        onClick={onSelect}
        className={cx(
            'w-full flex items-center gap-3 px-2 py-1.5 rounded-xl transition-colors text-left',
            selected ? 'bg-black/[0.06]' : 'hover:bg-black/5 active:bg-black/10',
        )}
        style={{ minHeight: ROW_MIN_HEIGHT }}
    >
        <RadioDot selected={selected} />
        <span
            className={cx(
                'leading-tight',
                selected ? 'text-black font-semibold' : 'text-black font-medium',
            )}
            style={{ fontSize: 'clamp(13px, 3.4vw, 15px)' }}
        >
            {label}
        </span>
    </button>
);

const Section: React.FC<{
    icon: ReactNode;
    title: string;
    /** Optional right-aligned slot for live values (e.g. "4.0x"). */
    trailing?: ReactNode;
    children: ReactNode;
}> = ({ icon, title, trailing, children }) => (
    <section className="py-2.5">
        <header className="flex items-center gap-2 mb-1.5 px-1">
            <span className="text-black/75 flex-shrink-0 flex items-center justify-center">
                {icon}
            </span>
            <h3
                className="text-black/75 font-bold uppercase flex-1"
                style={{
                    fontSize: 'clamp(10px, 2.6vw, 12px)',
                    letterSpacing: '0.12em',
                }}
            >
                {title}
            </h3>
            {trailing && (
                <span
                    className="text-black font-mono font-bold flex-shrink-0"
                    style={{ fontSize: 'clamp(12px, 3.2vw, 14px)' }}
                >
                    {trailing}
                </span>
            )}
        </header>
        <div className="space-y-0.5">{children}</div>
    </section>
);

// ─── Settings drawer ───────────────────────────────────────────────

const SettingsBottomSheet: React.FC<SettingsBottomSheetProps> = ({ open, onClose }) => {
    const logout = useStore((state: SettingsDrawerStore) => state.logout);
    const handleBack = useStore((state: SettingsDrawerStore) => state.handleBack);
    const currentScreen = useStore((state: SettingsDrawerStore) => state.screen);
    const setCurrentScreen = useStore((state: SettingsDrawerStore) => state.setCurrentScreen);
    const org = useStore((state: SettingsDrawerStore) => state.org);
    const orgRole = useStore((state: SettingsDrawerStore) => state.orgRole);
    const companyLogo = useStore((state: SettingsDrawerStore) => state.companyLogo);
    const setCompanyLogo = useStore((state: SettingsDrawerStore) => state.setCompanyLogo);
    const companyLogoInputRef = useRef<HTMLInputElement>(null);
    const [orgFetchState, setOrgFetchState] = useState<'idle' | 'loading' | 'error'>('idle');
    const [orgFetchError, setOrgFetchError] = useState<string>('');

    const handleCompanyLogoUpload = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            alert('Logo file must be less than 5MB');
            return;
        }
        const reader = new FileReader();
        reader.onload = (event) => {
            const dataUri = event.target?.result;
            if (typeof dataUri === 'string') setCompanyLogo(dataUri);
        };
        // Keep the original silent-to-user behavior (no new alert text); just
        // record the failure so it isn't completely invisible.
        reader.onerror = () => logger.warn('Failed to read company logo file');
        try {
            reader.readAsDataURL(file);
        } catch {
            alert('Could not read the logo file.');
        }
    };

    const handleCompanyLogoDelete = (): void => {
        if (window.confirm('Are you sure you want to delete the company logo?')) {
            setCompanyLogo(null);
        }
    };

    // Refresh org state whenever the drawer opens — self-healing if
    // localStorage holds stale claims after an auth refresh.
    useEffect(() => {
        if (!open) return;
        const refresh = async () => {
            setOrgFetchState('loading');
            setOrgFetchError('');
            try {
                await (useStore.getState() as SettingsDrawerStore).fetchOrgData();
                setOrgFetchState('idle');
            } catch (e: unknown) {
                setOrgFetchError(errorMessage(e) || 'Failed to refresh org');
                setOrgFetchState('error');
            }
        };
        refresh();
    }, [open]);

    // Zustand state for mic/annotation settings
    const autoMicEnabled = useStore((state: SettingsDrawerStore) => state.autoMicEnabled);
    const setAutoMicEnabled = useStore((state: SettingsDrawerStore) => state.setAutoMicEnabled);
    const annotationModeEnabled = useStore((state: SettingsDrawerStore) => state.annotationModeEnabled);
    const setAnnotationModeEnabled = useStore((state: SettingsDrawerStore) => state.setAnnotationModeEnabled);
    const showMicButtonOnCamera = useStore((state: SettingsDrawerStore) => state.showMicButtonOnCamera);
    const setShowMicButtonOnCamera = useStore((state: SettingsDrawerStore) => state.setShowMicButtonOnCamera);
    const showAnnotationButtonOnCamera = useStore((state: SettingsDrawerStore) => state.showAnnotationButtonOnCamera);
    const setShowAnnotationButtonOnCamera = useStore((state: SettingsDrawerStore) => state.setShowAnnotationButtonOnCamera);

    // Local state for storage-backed settings
    const [simplePhotoMode, setSimplePhotoMode] = useState<SimplePhotoMode>('pincam');
    const [zoomRatio, setZoomRatio] = useState<number>(4.0);
    const [showZoomMinimap, setShowZoomMinimap] = useState<boolean>(true);
    const [showGridOverlay, setShowGridOverlay] = useState<boolean>(false);

    // Hydrate local state from storage when the drawer opens.
    useEffect(() => {
        if (!open) return;
        try {
            const simpleMode = safeStorage.getItem('pinCam_simplePhotoMode') || 'pincam';
            if (['none', 'pincam', 'company'].includes(simpleMode)) {
                setSimplePhotoMode(simpleMode as SimplePhotoMode);
            }
            const savedZoomRatio = safeStorage.getItem('pinCam_zoomRatio');
            const parsedZoom = parseFloat(savedZoomRatio ?? '');
            if (Number.isFinite(parsedZoom)) setZoomRatio(parsedZoom);
            const savedShowZoom = safeStorage.getItem('pinCam_showZoomMini');
            if (savedShowZoom !== null) setShowZoomMinimap(savedShowZoom !== '0');
            const savedGridOverlay = safeStorage.getItem('pinCam_showGridOverlay');
            if (savedGridOverlay !== null) setShowGridOverlay(savedGridOverlay === 'true');
        } catch {
            /* defaults already set */
        }
    }, [open]);

    const saveAndClose = (): void => {
        try {
            safeStorage.setItem('pinCam_simplePhotoMode', simplePhotoMode);
            safeStorage.setItem('pinCam_zoomRatio', zoomRatio.toString());
            safeStorage.setItem('pinCam_showZoomMini', showZoomMinimap ? '1' : '0');
            safeStorage.setItem('pinCam_showGridOverlay', showGridOverlay.toString());
            if (simplePhotoMode === 'company') {
                safeStorage.setItem('pinCam_includeCompanyLogo', 'true');
            } else {
                safeStorage.removeItem('pinCam_includeCompanyLogo');
            }
            window.dispatchEvent(new Event('pinCam:settingsChanged'));
        } catch {
            /* settings save failed */
        }
        onClose();
    };

    // Footer: Back (when not already on the projects/admin screens) +
    // Logout. Both small ghost buttons — secondary actions, not CTAs.
    const footer = (
        <div className="flex items-center gap-2">
            {currentScreen !== 'projects' && currentScreen !== 'admin-panel' && (
                <button
                    type="button"
                    onClick={() => {
                        saveAndClose();
                        handleBack();
                    }}
                    className="flex items-center justify-center gap-2 px-3 rounded-xl border border-black/30 bg-black/5 text-black hover:bg-black/10 active:bg-black/15 font-bold transition-colors"
                    style={{ minHeight: ROW_MIN_HEIGHT, fontSize: 'clamp(13px, 3.4vw, 15px)' }}
                >
                    <ArrowLeft size={16} />
                    Back
                </button>
            )}
            <button
                type="button"
                onClick={() => {
                    logout();
                    onClose();
                }}
                className="ml-auto flex items-center justify-center gap-2 px-3 rounded-xl border border-black/30 bg-black/5 text-black hover:bg-black/10 active:bg-black/15 font-bold transition-colors"
                style={{ minHeight: ROW_MIN_HEIGHT, fontSize: 'clamp(13px, 3.4vw, 15px)' }}
            >
                <LogOut size={16} />
                Logout
            </button>
        </div>
    );

    return (
        <BottomDrawer
            open={open}
            onClose={saveAndClose}
            title="Settings"
            maxHeight="85dvh"
            footer={footer}
        >
            {/* Account row — Subscription (primary, black pill, yellow
                ink) + Organization (secondary, ghost). Side-by-side
                via flex-1 each so the row stays compact on mobile.
                Org label drops the org name to fit half-width; the
                full name is visible inside the org screen. */}
            <div className="pt-1 pb-3 space-y-2">
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={() => {
                            saveAndClose();
                            setCurrentScreen('billing');
                        }}
                        className="flex-1 min-w-0 flex items-center justify-center gap-1.5 px-2 py-1.5 bg-black hover:bg-black/90 active:scale-[0.98] text-yellow-300 font-bold rounded-xl border-2 border-black transition-all"
                        style={{
                            minHeight: ROW_MIN_HEIGHT,
                            fontSize: 'clamp(11px, 3vw, 14px)',
                        }}
                    >
                        <CreditCard
                            className="flex-shrink-0"
                            style={{ width: 'clamp(14px, 3.6vw, 16px)', height: 'clamp(14px, 3.6vw, 16px)' }}
                        />
                        <span className="leading-tight text-center break-words">Subscription</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            saveAndClose();
                            setCurrentScreen('organization');
                        }}
                        className="flex-1 min-w-0 flex items-center justify-center gap-1.5 px-2 py-1.5 bg-black/5 hover:bg-black/10 active:bg-black/15 text-black font-semibold rounded-xl border border-black/30 transition-colors"
                        style={{
                            minHeight: ROW_MIN_HEIGHT,
                            fontSize: 'clamp(11px, 3vw, 14px)',
                        }}
                    >
                        <Building2
                            className="flex-shrink-0"
                            style={{ width: 'clamp(14px, 3.6vw, 16px)', height: 'clamp(14px, 3.6vw, 16px)' }}
                        />
                        <span className="leading-tight text-center break-words">
                            {orgFetchState === 'loading'
                                ? 'Checking…'
                                : org
                                    ? 'Organization'
                                    : 'Create organization'}
                        </span>
                    </button>
                </div>
                {orgFetchState === 'error' && (
                    <p
                        className="text-red-700 px-1 leading-snug"
                        style={{ fontSize: 'clamp(11px, 2.8vw, 12px)' }}
                    >
                        Couldn't load org: {orgFetchError}
                    </p>
                )}
            </div>

            <div className="divide-y divide-black/10">
                {/* Company logo — replaces the dedicated WatermarkScreen
                    which only hosted this control. Lives in the drawer
                    now so users don't navigate away from settings. */}
                <Section icon={<ImageIcon size={15} />} title="Company logo">
                    {org ? (
                        <div className="px-2 py-1 space-y-2">
                            {org.company_logo && (
                                <img
                                    src={org.company_logo}
                                    alt="Org logo"
                                    className="h-14 max-w-32 object-contain rounded-xl border border-black/25 bg-white p-1"
                                />
                            )}
                            <p
                                className="text-black/70 leading-relaxed"
                                style={{ fontSize: 'clamp(11px, 2.8vw, 12px)' }}
                            >
                                Logo is managed by your organization.
                                {orgRole === 'owner' && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            saveAndClose();
                                            setCurrentScreen('organization');
                                        }}
                                        className="text-black underline ml-1 font-semibold"
                                    >
                                        Edit in Organization settings
                                    </button>
                                )}
                            </p>
                        </div>
                    ) : (
                        <div className="px-2 py-1 space-y-2">
                            <p
                                className="text-black/70 leading-relaxed"
                                style={{ fontSize: 'clamp(11px, 2.8vw, 12px)' }}
                            >
                                Appears in every watermark. Upload once — it syncs to your other devices automatically.
                            </p>

                            <input
                                type="file"
                                ref={companyLogoInputRef}
                                accept="image/*"
                                onChange={handleCompanyLogoUpload}
                                className="hidden"
                            />

                            {companyLogo ? (
                                <div className="flex items-center gap-3">
                                    <img
                                        src={companyLogo}
                                        alt="Company Logo"
                                        className="h-14 max-w-32 object-contain rounded-xl border border-black/25 bg-white p-1"
                                    />
                                    <div className="flex flex-col gap-1.5">
                                        <button
                                            type="button"
                                            onClick={() => companyLogoInputRef.current?.click()}
                                            className="flex items-center gap-1.5 px-2.5 rounded-xl border border-black/30 bg-black/5 text-black hover:bg-black/10 active:bg-black/15 font-medium transition-colors"
                                            style={{
                                                minHeight: 'min(8vw, 34px)',
                                                fontSize: 'clamp(11px, 2.8vw, 13px)',
                                            }}
                                        >
                                            <Upload size={13} />
                                            Replace
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleCompanyLogoDelete}
                                            className="flex items-center gap-1.5 px-2.5 rounded-xl border border-red-700/40 bg-red-500/10 text-red-800 hover:bg-red-500/15 active:bg-red-500/20 font-medium transition-colors"
                                            style={{
                                                minHeight: 'min(8vw, 34px)',
                                                fontSize: 'clamp(11px, 2.8vw, 13px)',
                                            }}
                                        >
                                            <Trash2 size={13} />
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => companyLogoInputRef.current?.click()}
                                    className="w-full flex items-center justify-center gap-2 rounded-xl border-2 border-black bg-black text-yellow-300 hover:bg-black/90 active:scale-[0.98] font-bold transition-all"
                                    style={{
                                        minHeight: ROW_MIN_HEIGHT,
                                        fontSize: 'clamp(13px, 3.4vw, 15px)',
                                    }}
                                >
                                    <Upload size={15} />
                                    Upload company logo
                                </button>
                            )}
                        </div>
                    )}
                </Section>

                <Section icon={<Mic size={15} />} title="Microphone">
                    <ToggleRow
                        label="Auto-start after arrow placement"
                        description="Recording begins automatically when you drop a pin"
                        enabled={autoMicEnabled}
                        onToggle={() => setAutoMicEnabled(!autoMicEnabled)}
                    />
                    <ToggleRow
                        label="Show shortcut on camera"
                        enabled={showMicButtonOnCamera}
                        onToggle={() => setShowMicButtonOnCamera(!showMicButtonOnCamera)}
                    />
                </Section>

                <Section icon={<PenTool size={15} />} title="Annotation">
                    <ToggleRow
                        label="Auto-start after arrow placement"
                        description="Drawing mode opens automatically when you drop a pin"
                        enabled={annotationModeEnabled}
                        onToggle={() => setAnnotationModeEnabled(!annotationModeEnabled)}
                    />
                    <ToggleRow
                        label="Show shortcut on camera"
                        enabled={showAnnotationButtonOnCamera}
                        onToggle={() => setShowAnnotationButtonOnCamera(!showAnnotationButtonOnCamera)}
                    />
                </Section>

                <Section icon={<ImageIcon size={15} />} title="Photos without project">
                    <RadioRow
                        label="No watermark (plain photo)"
                        selected={simplePhotoMode === 'none'}
                        onSelect={() => setSimplePhotoMode('none')}
                    />
                    <RadioRow
                        label="User info"
                        selected={simplePhotoMode === 'company'}
                        onSelect={() => setSimplePhotoMode('company')}
                    />
                </Section>

                <Section icon={<MapIcon size={15} />} title="Zoomed minimap">
                    <ToggleRow
                        label="Include zoomed minimap under the full floorplan"
                        enabled={showZoomMinimap}
                        onToggle={() => setShowZoomMinimap(!showZoomMinimap)}
                    />
                </Section>

                <Section icon={<Grid3x3 size={15} />} title="Grid overlay">
                    <ToggleRow
                        label="Show grid"
                        enabled={showGridOverlay}
                        onToggle={() => setShowGridOverlay(!showGridOverlay)}
                    />
                </Section>

                <Section
                    icon={<ZoomIn size={15} />}
                    title="Minimap zoom ratio"
                    trailing={`${zoomRatio.toFixed(1)}x`}
                >
                    <div className="flex items-center gap-3 px-2 py-1.5">
                        <span
                            className="text-black/55 font-mono flex-shrink-0"
                            style={{ fontSize: 'clamp(11px, 2.8vw, 12px)' }}
                        >
                            2x
                        </span>
                        <input
                            type="range"
                            min="2"
                            max="16"
                            step="0.5"
                            value={zoomRatio}
                            onChange={(e) => setZoomRatio(parseFloat(e.target.value))}
                            className="flex-1"
                            style={{
                                accentColor: '#000000',
                            }}
                            aria-label="Minimap zoom ratio"
                        />
                        <span
                            className="text-black/55 font-mono flex-shrink-0"
                            style={{ fontSize: 'clamp(11px, 2.8vw, 12px)' }}
                        >
                            16x
                        </span>
                    </div>
                </Section>
            </div>
        </BottomDrawer>
    );
};

export default SettingsBottomSheet;
